import axios from 'axios';
import { nextEventLoop } from 'helpers/timing';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static values = {
    src: String,
    loading: {
      type: String,
      default: 'eager',
    },
  };

  controller = new AbortController();

  initialize() {
    this.intersectionObserver = new IntersectionObserver(this.intersect.bind(this));

    if (this.loadingValue === 'eager') this.makeRequest();
    if (this.loadingValue === 'lazy') this.intersectionObserver.observe(this.element);
  }

  srcValueChanged(newValue, oldValue) {
    // make request only when the oldValue was present and it changed
    if (oldValue && newValue) {
      this.makeRequest();
    }
  }

  async makeRequest() {
    if (!this.hasSrcValue || !this.srcValue) return;

    this.controller?.abort();
    this.controller = new AbortController();

    this.clearObserver();
    try {
      const data = await makeNetworkRequest(this.element, this.srcValue, {
        headers: {
          accept: 'text/fragment+html',
          credentials: 'same-origin',
        },
        signal: this.controller.signal,
      });
      this.targetContainer.innerHTML = data;
      this.dispatch('success', { bubbles: true });
      this.controller = null;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.dispatch('error', { bubbles: true });
        this.controller = null;
      }
    }
  }

  // Private

  intersect(entries) {
    const lastEntry = entries.slice(-1)[0];
    if (lastEntry?.isIntersecting) {
      const { target } = lastEntry;

      this.clearObserver(target);
      this.makeRequest();
    }
  }

  clearObserver(target = this.element) {
    this.intersectionObserver?.unobserve(target);
  }

  get targetContainer() {
    const element = this.element.getAttribute('aria-controls');
    return document.getElementById(element) || this.element;
  }
}

async function makeNetworkRequest(el, src, options) {
  // Mimic `<img>` event dispatch order ['loadstart', 'load/error', 'loadend']
  try {
    el.dispatchEvent(new CustomEvent('loadstart'));
    const { data } = await axios.get(src, options);
    el.dispatchEvent(new CustomEvent('load'));
    el.dispatchEvent(new CustomEvent('loadend'));
    await nextEventLoop();
    return data;
  } catch (error) {
    el.dispatchEvent(new CustomEvent('error'));
    el.dispatchEvent(new CustomEvent('loadend'));
    await nextEventLoop();
    throw error;
  }
}
